import {
    AddOutlined,
    DeleteOutline,
    EditOutlined,
    LayersOutlined,
} from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SelectElement } from "@types";
import { isAxiosError } from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { PageHeader } from "src/components";
import { AdminConfigurationNav } from "src/components/AdminConfigurationNav";
import type { RawEditorHandle } from "src/components/DocViewer/components/markdownDocViewer/rawEditor/RawEditor";
import RawEditor from "src/components/DocViewer/components/markdownDocViewer/rawEditor/RawEditor";
import { Select as FormSelect } from "src/components/Form/Elements/Select";
import { PageBreadcrumb } from "src/components/PageBreadcrumb";
import { useSnackbar } from "src/components/Snackbar";
import {
    getAdminDocumentTemplatesQueryKey,
    getAdminTaskDefinitionsQueryKey,
} from "src/config/request-keys";
import {
    useGetAdminDevices,
    useGetAdminOrganizations,
    useGetDevices,
    useGetRoadmapCards,
    useGetRoadmapStages,
    useGetUser,
} from "src/hooks";
import { useDebounce } from "src/hooks/useDebounce";
import {
    checkTaskKeyExists,
    configureTaskDefinitionDisabledTargetsAdmin,
    createTaskDefinition,
    deleteTaskDefinition,
    getAdminDocumentTemplates,
    getAdminTaskDefinitions,
    updateTaskDefinition,
} from "src/services/backend";
import {
    DocumentTemplate,
    TaskDefinition,
    TaskDefinitionType,
    TaskStateScope,
    documentTemplateSchemas,
    taskDefinitionsSchema,
} from "src/stores/models";
import { RoadmapVisibilityConditions } from "src/stores/models/roadmap-visibility-conditions";
import { isTaskTypeEditable } from "src/utils/tasks";

const getApiErrorMessage = (error: unknown, fallback: string): string => {
    if (isAxiosError(error) && typeof error.response?.data === "string") {
        return error.response.data;
    }
    return fallback;
};

type CardOption = {
    cardKey: string;
    title: string;
    stageTitle: string;
};

type DefinitionGroup = {
    taskKey: string;
    base: TaskDefinition | null;
    overrides: TaskDefinition[];
    definitions: TaskDefinition[];
};

const getDefinitionGroupDisabledTargets = (group: DefinitionGroup) => {
    const organizationIds = new Set<string>();
    const deviceIds = new Set<string>();

    group.definitions.forEach((definition) => {
        definition.disabledForOrganizations?.forEach((organization) => {
            organizationIds.add(organization.id);
        });
        definition.disabledForDevices?.forEach((device) => {
            deviceIds.add(device.id);
        });
    });

    return {
        organizationIds: Array.from(organizationIds),
        deviceIds: Array.from(deviceIds),
    };
};

const VISIBILITY_CONDITION_OPTIONS: Array<{
    label: string;
    key: keyof RoadmapVisibilityConditions;
}> = [
        { key: "isMDR", label: "MDR" },
        { key: "isIVDR", label: "IVDR" },
        { key: "isFDA", label: "FDA" },
        { key: "isUK", label: "UK" },
        { key: "isCanada", label: "Canada" },
        { key: "isClass1", label: "Class I" },
        { key: "isClass2a", label: "Class IIa" },
        { key: "isClass2b", label: "Class IIb" },
        { key: "isClass3", label: "Class III" },
        { key: "hasHardware", label: "Hardware" },
        { key: "hasSoftware", label: "Software" },
        { key: "hasElectrical", label: "Electrical" },
        { key: "hasSterile", label: "Sterile" },
        { key: "hasFirmware", label: "Firmware" },
        { key: "hasMechanical", label: "Mechanical" },
        { key: "hasImplantable", label: "Implantable" },
        { key: "isSoftwareOnly", label: "Software-only" },
    ];

const formatScopeLabel = (
    definition: TaskDefinition,
    orgNameById: Map<string, string>,
    deviceById: Map<string, { name: string; organizationName?: string | null }>,
) => {
    if (!definition.organizationId && !definition.deviceId) {
        return "Base (Global)";
    }
    if (definition.deviceId) {
        const device = deviceById.get(definition.deviceId);
        if (device?.organizationName) {
            return `Device override: ${device.organizationName} — ${device.name}`;
        }
        return `Device override`;
    }
    if (definition.organizationId) {
        const orgName = orgNameById.get(definition.organizationId);
        return orgName ? `Organization override: ${orgName}` : "Organization override";
    }
    return "Override";
};

export const AdminTaskDefinitions = () => {
    const queryClient = useQueryClient();
    const { orgId = "" } = useParams();
    const { showSnackbar } = useSnackbar();
    const { data: user } = useGetUser();
    const canManage = !!user?.permissions?.includes("write:configuration");

    const { data: devices } = useGetDevices({ orgId });
    const { data: adminOrganizations = [] } = useGetAdminOrganizations({
        enabled: canManage,
    });
    const { data: adminDevices = [] } = useGetAdminDevices({
        enabled: canManage,
    });

    const [search, setSearch] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<"base" | "override" | "edit">(
        "base",
    );
    const [editingDefinition, setEditingDefinition] =
        useState<TaskDefinition | null>(null);
    const [overrideBase, setOverrideBase] = useState<TaskDefinition | null>(null);
    const [createScope, setCreateScope] = useState<
        "global" | "organization" | "device"
    >("global");
    const [createOrganizationId, setCreateOrganizationId] = useState<string>("");
    const [createDeviceId, setCreateDeviceId] = useState<string>("");
    const [overrideScope, setOverrideScope] = useState<"organization" | "device">(
        "organization",
    );
    const [overrideOrganizationId, setOverrideOrganizationId] = useState<string>("");
    const [overrideDeviceId, setOverrideDeviceId] = useState<string>("");
    const [taskType, setTaskType] = useState<TaskDefinitionType>(
        TaskDefinitionType.MANUAL,
    );
    const [taskKey, setTaskKey] = useState("");
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const descriptionEditorRef = useRef<RawEditorHandle | null>(null);
    const [enabled, setEnabled] = useState(true);
    const [order, setOrder] = useState<string>("");
    const [manualScope, setManualScope] = useState<TaskStateScope>(
        TaskStateScope.DEVICE,
    );
    const [selectedTemplate, setSelectedTemplate] =
        useState<DocumentTemplate | null>(null);
    const [selectedCardOption, setSelectedCardOption] =
        useState<CardOption | null>(null);
    const [visibilityConditions, setVisibilityConditions] =
        useState<RoadmapVisibilityConditions>({});
    const [formError, setFormError] = useState<string | null>(null);
    const [taskKeyError, setTaskKeyError] = useState<string | null>(null);
    const taskKeyCheckIdRef = useRef(0);
    const [confirmDelete, setConfirmDelete] = useState<TaskDefinition | null>(null);
    const [configureDisabledGroup, setConfigureDisabledGroup] =
        useState<DefinitionGroup | null>(null);
    const [configuredOrganizationIds, setConfiguredOrganizationIds] = useState<
        string[]
    >([]);
    const [configuredDeviceIds, setConfiguredDeviceIds] = useState<string[]>([]);
    const [configureError, setConfigureError] = useState<string | null>(null);

    const cardsDeviceId = devices?.[0]?.id ?? "";
    const { data: cards } = useGetRoadmapCards({
        orgId,
        deviceId: cardsDeviceId,
        enabled: !!cardsDeviceId,
    });
    const { data: stages } = useGetRoadmapStages({
        orgId,
        deviceId: cardsDeviceId,
        enabled: !!cardsDeviceId,
    });

    const cardOptions: CardOption[] = useMemo(() => {
        if (!cards || !stages) return [];
        return cards
            .map((card) => {
                const stage = stages.find((s) => s.stageKey === card.parentStageKey);
                return {
                    cardKey: card.cardKey,
                    title: card.title,
                    stageTitle: stage?.title ?? "Unknown Stage",
                };
            })
            .sort((a, b) => {
                const stageCompare = a.stageTitle.localeCompare(b.stageTitle);
                if (stageCompare !== 0) return stageCompare;
                return a.title.localeCompare(b.title);
            });
    }, [cards, stages]);

    const { data: definitions = [], isLoading } = useQuery({
        queryKey: getAdminTaskDefinitionsQueryKey(),
        queryFn: async () => {
            const { data } = await getAdminTaskDefinitions();
            return taskDefinitionsSchema.validate(data);
        },
        enabled: canManage,
    });

    const { data: templates = [] } = useQuery({
        queryKey: getAdminDocumentTemplatesQueryKey({
            scope: "global",
        }),
        queryFn: async () => {
            const { data } = await getAdminDocumentTemplates({
                scope: "global",
            });
            return documentTemplateSchemas.validate(data);
        },
        enabled: canManage && taskType === TaskDefinitionType.DOCUMENT,
    });

    const orgNameById = useMemo(() => {
        return new Map(adminOrganizations.map((org) => [org.id, org.name]));
    }, [adminOrganizations]);

    const deviceById = useMemo(() => {
        return new Map(
            adminDevices.map((device) => [
                device.id,
                { name: device.name, organizationName: device.organizationName },
            ]),
        );
    }, [adminDevices]);

    const buildSelectElement = (id: string, label: string, options: string[]): SelectElement => ({
        id,
        type: "select",
        options: {
            label,
            options,
        },
    });

    const taskTypeOptions: Array<{ value: TaskDefinitionType; label: string }> = [
        { value: TaskDefinitionType.MANUAL, label: "Manual" },
        { value: TaskDefinitionType.DOCUMENT, label: "Document" },
    ];
    const taskTypeLabel =
        taskTypeOptions.find((option) => option.value === taskType)?.label ?? "";

    const manualScopeOptions: Array<{ value: TaskStateScope; label: string }> = [
        { value: TaskStateScope.ORGANIZATION, label: "Organization" },
        { value: TaskStateScope.DEVICE, label: "Device" },
    ];
    const manualScopeLabel =
        manualScopeOptions.find((option) => option.value === manualScope)?.label ?? "";

    const overrideScopeOptions: Array<{ value: "organization" | "device"; label: string }> = [
        { value: "organization", label: "Organization" },
        { value: "device", label: "Device" },
    ];
    const overrideScopeLabel =
        overrideScopeOptions.find((option) => option.value === overrideScope)?.label ??
        "";
    const createScopeOptions: Array<{
        value: "global" | "organization" | "device";
        label: string;
    }> = [
            { value: "global", label: "Global" },
            { value: "organization", label: "Organization" },
            { value: "device", label: "Device" },
        ];
    const createScopeLabel =
        createScopeOptions.find((option) => option.value === createScope)?.label ??
        "";

    const selectedOverrideOrganization = useMemo(() => {
        return (
            adminOrganizations.find((org) => org.id === overrideOrganizationId) ??
            null
        );
    }, [adminOrganizations, overrideOrganizationId]);

    const selectedCreateOrganization = useMemo(() => {
        return (
            adminOrganizations.find((org) => org.id === createOrganizationId) ??
            null
        );
    }, [adminOrganizations, createOrganizationId]);

    const selectedOverrideDevice = useMemo(() => {
        return (
            adminDevices.find((device) => device.id === overrideDeviceId) ?? null
        );
    }, [adminDevices, overrideDeviceId]);

    const selectedCreateDevice = useMemo(() => {
        return adminDevices.find((device) => device.id === createDeviceId) ?? null;
    }, [adminDevices, createDeviceId]);

    const selectedConfiguredOrganizations = useMemo(
        () =>
            adminOrganizations.filter((organization) =>
                configuredOrganizationIds.includes(organization.id),
            ),
        [adminOrganizations, configuredOrganizationIds],
    );

    const selectedConfiguredDevices = useMemo(
        () => adminDevices.filter((device) => configuredDeviceIds.includes(device.id)),
        [adminDevices, configuredDeviceIds],
    );
    const resolvedCreateOrganizationId =
        createScope === "device"
            ? createOrganizationId || selectedCreateDevice?.organizationId || ""
            : createOrganizationId;

    const groupedDefinitions = useMemo<DefinitionGroup[]>(() => {
        const map = new Map<string, TaskDefinition[]>();
        definitions.forEach((definition) => {
            const list = map.get(definition.taskKey) ?? [];
            list.push(definition);
            map.set(definition.taskKey, list);
        });
        return Array.from(map.entries()).map(([taskKey, defs]) => {
            const base = defs.find(
                (definition) => !definition.organizationId && !definition.deviceId,
            );
            const overrides = defs
                .filter((definition) => definition !== base)
                .sort((a, b) => {
                    if (a.deviceId && !b.deviceId) return 1;
                    if (!a.deviceId && b.deviceId) return -1;
                    return a.title.localeCompare(b.title);
                });
            return { taskKey, base: base ?? null, overrides, definitions: defs };
        });
    }, [definitions]);

    const filteredGroups = useMemo(() => {
        if (!search.trim()) return groupedDefinitions;
        const query = search.toLowerCase();
        return groupedDefinitions.filter(
            (group) =>
                group.taskKey.toLowerCase().includes(query) ||
                group.definitions.some((def) =>
                    def.title.toLowerCase().includes(query),
                ),
        );
    }, [groupedDefinitions, search]);

    const invalidateTaskDefinitionDependentQueries = async () => {
        await queryClient.invalidateQueries({
            queryKey: ["roadmapTasks", orgId],
            exact: false,
        });
        await queryClient.invalidateQueries({
            queryKey: ["roadmapCards", orgId],
            exact: false,
        });
        await queryClient.invalidateQueries({
            queryKey: ["roadmapStages", orgId],
            exact: false,
        });
    };

    const createMutation = useMutation({
        mutationFn: createTaskDefinition,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: getAdminTaskDefinitionsQueryKey(),
            });
            await invalidateTaskDefinitionDependentQueries();
            handleCloseDialog();
        },
        onError: (error: unknown) => {
            const message = getApiErrorMessage(error, "Failed to save task definition.");
            setFormError(message);
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({
            taskId,
            data,
        }: {
            taskId: string;
            data: Parameters<typeof updateTaskDefinition>[1];
        }) => updateTaskDefinition(taskId, data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: getAdminTaskDefinitionsQueryKey(),
            });
            await invalidateTaskDefinitionDependentQueries();
            handleCloseDialog();
        },
        onError: (error: unknown) => {
            const message = getApiErrorMessage(error, "Failed to update task definition.");
            setFormError(message);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteTaskDefinition,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: getAdminTaskDefinitionsQueryKey(),
            });
            await invalidateTaskDefinitionDependentQueries();
            setConfirmDelete(null);
        },
        onError: () => {
            showSnackbar("Failed to delete task definition.");
        },
    });

    const configureDisabledTargetsMutation = useMutation({
        mutationFn: async ({
            taskKey,
            organizationIds,
            deviceIds,
        }: {
            taskKey: string;
            organizationIds: string[];
            deviceIds: string[];
        }) => {
            await Promise.all([
                configureTaskDefinitionDisabledTargetsAdmin({
                    taskKey,
                    scope: "organization",
                    organizationIds,
                    deviceIds: [],
                }),
                configureTaskDefinitionDisabledTargetsAdmin({
                    taskKey,
                    scope: "device",
                    organizationIds: [],
                    deviceIds,
                }),
            ]);
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: getAdminTaskDefinitionsQueryKey(),
            });
            await invalidateTaskDefinitionDependentQueries();
            setConfigureDisabledGroup(null);
            setConfigureError(null);
        },
        onError: () => {
            setConfigureError("Failed to update disabled targets.");
        },
    });

    const handleTaskKeyCheck = useCallback(
        async ({
            taskKey: key,
            organizationId,
            deviceId,
            errorMessage,
            checkId,
        }: {
            taskKey: string;
            organizationId: string | null;
            deviceId: string | null;
            errorMessage: string;
            checkId: number;
        }) => {
            try {
                const exists = await checkTaskKeyExists({
                    taskKey: key,
                    organizationId,
                    deviceId,
                });
                if (taskKeyCheckIdRef.current !== checkId) return;
                setTaskKeyError(exists ? errorMessage : null);
            } catch {
                if (taskKeyCheckIdRef.current !== checkId) return;
                setTaskKeyError("Unable to validate task key.");
            }
        },
        [],
    );

    const { debounce: debounceTaskKeyCheck, cancelDebounce: cancelDebounceTaskKeyCheck } =
        useDebounce(300, handleTaskKeyCheck);

    useEffect(() => {
        if (!selectedCardOption) return;
        if (
            selectedCardOption.cardKey &&
            cardOptions.some((card) => card.cardKey === selectedCardOption.cardKey)
        ) {
            return;
        }
        setSelectedCardOption(
            cardOptions.find((card) => card.cardKey === selectedCardOption.cardKey) ??
            null,
        );
    }, [cardOptions, selectedCardOption]);

    useEffect(() => {
        if (!dialogOpen) return;
        if (editingDefinition?.parentCardKey) {
            setSelectedCardOption(
                cardOptions.find(
                    (card) => card.cardKey === editingDefinition.parentCardKey,
                ) ?? null,
            );
        }
    }, [cardOptions, dialogOpen, editingDefinition]);

    useEffect(() => {
        if (taskType === TaskDefinitionType.MANUAL) {
            setSelectedTemplate(null);
        }
    }, [taskType]);

    useEffect(() => {
        if (dialogMode !== "base") return;
        if (createScope === "global") {
            setCreateOrganizationId("");
            setCreateDeviceId("");
        }
        if (createScope === "organization") {
            setCreateDeviceId("");
        }
        if (createScope === "device") {
            setCreateOrganizationId("");
        }
    }, [dialogMode, createScope]);

    useEffect(() => {
        if (dialogMode !== "override") return;
        if (overrideScope === "organization") {
            setOverrideDeviceId("");
        }
        if (overrideScope === "device") {
            setOverrideOrganizationId("");
        }
    }, [dialogMode, overrideScope]);

    useEffect(() => {
        if (!dialogOpen) return;
        if (dialogMode === "edit") {
            setTaskKeyError(null);
            cancelDebounceTaskKeyCheck();
            return;
        }

        if (dialogMode === "base") {
            const trimmedKey = taskKey.trim();
            if (!trimmedKey) {
                setTaskKeyError(null);
                cancelDebounceTaskKeyCheck();
                return;
            }

            if (createScope === "global") {
                setTaskKeyError(null);
                const checkId = ++taskKeyCheckIdRef.current;
                debounceTaskKeyCheck({
                    taskKey: trimmedKey,
                    organizationId: null,
                    deviceId: null,
                    errorMessage: `A task definition with key "${trimmedKey}" already exists at global scope.`,
                    checkId,
                });
                return;
            }

            if (createScope === "organization") {
                if (!createOrganizationId) {
                    setTaskKeyError(null);
                    cancelDebounceTaskKeyCheck();
                    return;
                }
                setTaskKeyError(null);
                const scopeLabel = `organization "${orgNameById.get(createOrganizationId) ?? createOrganizationId}"`;
                const checkId = ++taskKeyCheckIdRef.current;
                debounceTaskKeyCheck({
                    taskKey: trimmedKey,
                    organizationId: createOrganizationId,
                    deviceId: null,
                    errorMessage: `A task definition with key "${trimmedKey}" already exists for ${scopeLabel}.`,
                    checkId,
                });
                return;
            }

            if (!resolvedCreateOrganizationId || !createDeviceId) {
                setTaskKeyError(null);
                cancelDebounceTaskKeyCheck();
                return;
            }
            setTaskKeyError(null);
            const scopeLabel = `device "${deviceById.get(createDeviceId)?.name ?? createDeviceId}"`;
            const checkId = ++taskKeyCheckIdRef.current;
            debounceTaskKeyCheck({
                taskKey: trimmedKey,
                organizationId: resolvedCreateOrganizationId,
                deviceId: createDeviceId,
                errorMessage: `A task definition with key "${trimmedKey}" already exists for ${scopeLabel}.`,
                checkId,
            });
            return;
        }

        if (dialogMode === "override" && overrideBase) {
            if (overrideScope === "organization") {
                if (!overrideOrganizationId) {
                    setTaskKeyError(null);
                    cancelDebounceTaskKeyCheck();
                    return;
                }
                setTaskKeyError(null);
                const scopeLabel = `organization "${orgNameById.get(overrideOrganizationId) ?? overrideOrganizationId}"`;
                const checkId = ++taskKeyCheckIdRef.current;
                debounceTaskKeyCheck({
                    taskKey: overrideBase.taskKey,
                    organizationId: overrideOrganizationId,
                    deviceId: null,
                    errorMessage: `An override for key "${overrideBase.taskKey}" already exists for ${scopeLabel}.`,
                    checkId,
                });
                return;
            }

            const resolvedOverrideOrganizationId =
                overrideOrganizationId || selectedOverrideDevice?.organizationId || "";
            if (!resolvedOverrideOrganizationId || !overrideDeviceId) {
                setTaskKeyError(null);
                cancelDebounceTaskKeyCheck();
                return;
            }
            setTaskKeyError(null);
            const scopeLabel = `device "${deviceById.get(overrideDeviceId)?.name ?? overrideDeviceId}"`;
            const checkId = ++taskKeyCheckIdRef.current;
            debounceTaskKeyCheck({
                taskKey: overrideBase.taskKey,
                organizationId: resolvedOverrideOrganizationId,
                deviceId: overrideDeviceId,
                errorMessage: `An override for key "${overrideBase.taskKey}" already exists for ${scopeLabel}.`,
                checkId,
            });
            return;
        }

        setTaskKeyError(null);
        cancelDebounceTaskKeyCheck();
    }, [
        dialogOpen,
        dialogMode,
        taskKey,
        createScope,
        createOrganizationId,
        createDeviceId,
        resolvedCreateOrganizationId,
        overrideBase,
        overrideScope,
        overrideOrganizationId,
        overrideDeviceId,
        selectedOverrideDevice,
        orgNameById,
        deviceById,
        debounceTaskKeyCheck,
        cancelDebounceTaskKeyCheck,
    ]);

    const resetForm = () => {
        setDialogMode("base");
        setEditingDefinition(null);
        setOverrideBase(null);
        setCreateScope("global");
        setCreateOrganizationId("");
        setCreateDeviceId("");
        setOverrideScope("organization");
        setOverrideOrganizationId("");
        setOverrideDeviceId("");
        setTaskType(TaskDefinitionType.MANUAL);
        setTaskKey("");
        setTitle("");
        setDescription("");
        descriptionEditorRef.current?.reloadContent("");
        setEnabled(true);
        setOrder("");
        setManualScope(TaskStateScope.DEVICE);
        setSelectedTemplate(null);
        setSelectedCardOption(null);
        setVisibilityConditions({});
        setFormError(null);
        setTaskKeyError(null);
    };

    const openBaseDialog = () => {
        resetForm();
        setDialogMode("base");
        setDialogOpen(true);
    };

    const openOverrideDialog = (baseDefinition: TaskDefinition) => {
        resetForm();
        setDialogMode("override");
        setOverrideBase(baseDefinition);
        setTaskType(baseDefinition.taskConfig.taskType as TaskDefinitionType);
        setTaskKey(baseDefinition.taskKey);
        setTitle(baseDefinition.title);
        setDescription(baseDefinition.description ?? "");
        descriptionEditorRef.current?.reloadContent(baseDefinition.description ?? "");
        setEnabled(baseDefinition.enabled);
        setOrder(
            baseDefinition.order !== null && baseDefinition.order !== undefined
                ? String(baseDefinition.order)
                : "",
        );
        setManualScope(baseDefinition.taskConfig.stateScope as TaskStateScope);
        setSelectedTemplate(
            templates.find((template) => template.templateKey === baseDefinition.taskKey) ??
            null,
        );
        setVisibilityConditions(
            (baseDefinition.visibilityConditions as RoadmapVisibilityConditions) ?? {},
        );
        setSelectedCardOption(
            cardOptions.find((card) => card.cardKey === baseDefinition.parentCardKey) ??
            null,
        );
        setDialogOpen(true);
    };

    const openEditDialog = (definition: TaskDefinition) => {
        resetForm();
        setDialogMode("edit");
        setEditingDefinition(definition);
        setTaskType(definition.taskConfig.taskType as TaskDefinitionType);
        setTaskKey(definition.taskKey);
        setTitle(definition.title);
        setDescription(definition.description ?? "");
        descriptionEditorRef.current?.reloadContent(definition.description ?? "");
        setEnabled(definition.enabled);
        setOrder(
            definition.order !== null && definition.order !== undefined
                ? String(definition.order)
                : "",
        );
        setManualScope(definition.taskConfig.stateScope as TaskStateScope);
        setSelectedTemplate(
            templates.find((template) => template.templateKey === definition.taskKey) ??
            null,
        );
        setVisibilityConditions(
            (definition.visibilityConditions as RoadmapVisibilityConditions) ?? {},
        );
        setSelectedCardOption(
            cardOptions.find((card) => card.cardKey === definition.parentCardKey) ??
            null,
        );
        if (definition.organizationId) {
            setOverrideScope(definition.deviceId ? "device" : "organization");
            setOverrideOrganizationId(definition.organizationId);
        }
        if (definition.deviceId) {
            setOverrideDeviceId(definition.deviceId);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        resetForm();
    };

    const openConfigureDisabledDialog = (group: DefinitionGroup) => {
        const { organizationIds, deviceIds } =
            getDefinitionGroupDisabledTargets(group);
        setConfigureDisabledGroup(group);
        setConfiguredOrganizationIds(organizationIds);
        setConfiguredDeviceIds(deviceIds);
        setConfigureError(null);
    };

    const handleCloseConfigureDisabledDialog = () => {
        setConfigureDisabledGroup(null);
        setConfigureError(null);
    };

    const toggleVisibility = (key: keyof RoadmapVisibilityConditions) => {
        setVisibilityConditions((prev) => {
            const next = { ...prev } as RoadmapVisibilityConditions;
            if (next[key]) {
                delete next[key];
            } else {
                next[key] = true;
            }
            return next;
        });
    };

    const getVisibilityPayload = () => {
        const keys = Object.keys(visibilityConditions);
        return keys.length > 0 ? visibilityConditions : null;
    };

    const handleSubmit = async () => {
        setFormError(null);
        const resolvedOverrideOrganizationId =
            overrideScope === "device"
                ? overrideOrganizationId || selectedOverrideDevice?.organizationId || ""
                : overrideOrganizationId;
        const resolvedCreateOrganizationIdForSubmit =
            createScope === "device"
                ? createOrganizationId || selectedCreateDevice?.organizationId || ""
                : createOrganizationId;

        if (!title.trim()) {
            setFormError("Title is required.");
            return;
        }

        if (!taskKey.trim()) {
            setFormError("Task key is required.");
            return;
        }

        if (taskKeyError) {
            setFormError(taskKeyError);
            return;
        }

        if (dialogMode === "base") {
            if (createScope === "organization" && !createOrganizationId) {
                setFormError("Select an organization for this definition.");
                return;
            }
            if (createScope === "device" && !resolvedCreateOrganizationIdForSubmit) {
                setFormError("Select a device tied to an organization.");
                return;
            }
            if (createScope === "device" && !createDeviceId) {
                setFormError("Select a device for this definition.");
                return;
            }
        }

        if (dialogMode === "override") {
            if (!overrideBase) {
                setFormError("Select a base task definition.");
                return;
            }
            if (overrideScope === "organization" && !overrideOrganizationId) {
                setFormError("Select an organization for this override.");
                return;
            }
            if (overrideScope === "device" && !resolvedOverrideOrganizationId) {
                setFormError("Select a device tied to an organization.");
                return;
            }
            if (overrideScope === "device" && !overrideDeviceId) {
                setFormError("Select a device for this override.");
                return;
            }
        }

        if (
            taskType === TaskDefinitionType.DOCUMENT &&
            dialogMode === "base" &&
            !selectedTemplate
        ) {
            setFormError("Select a document template.");
            return;
        }

        if (taskType === TaskDefinitionType.MANUAL && !manualScope) {
            setFormError("Select a manual task scope.");
            return;
        }

        const parentCardKey = selectedCardOption?.cardKey ?? null;
        const descriptionMarkdown =
            descriptionEditorRef.current?.getMarkdownContent() ?? description;
        const payload = {
            title: title.trim(),
            parentCardKey,
            description:
                taskType === TaskDefinitionType.MANUAL
                    ? descriptionMarkdown.trim() || null
                    : null,
            enabled,
            order: order ? Number(order) : null,
            visibilityConditions: getVisibilityPayload(),
        };

        if (dialogMode === "edit" && editingDefinition) {
            updateMutation.mutate({
                taskId: editingDefinition.id,
                data: payload,
            });
            return;
        }

        if (dialogMode === "override" && overrideBase) {
            const overridePayload = {
                ...payload,
                taskKey: overrideBase.taskKey,
                taskType: overrideBase.taskConfig.taskType,
                stateScope:
                    overrideBase.taskConfig.taskType === TaskDefinitionType.MANUAL
                        ? manualScope
                        : undefined,
                ...(overrideScope === "organization"
                    ? { organizationId: overrideOrganizationId }
                    : {}),
                ...(overrideScope === "device"
                    ? {
                        organizationId: resolvedOverrideOrganizationId,
                        deviceId: overrideDeviceId,
                    }
                    : {}),
            };
            createMutation.mutate(overridePayload);
            return;
        }

        createMutation.mutate({
            ...payload,
            taskKey:
                taskType === TaskDefinitionType.DOCUMENT
                    ? selectedTemplate?.templateKey ?? taskKey.trim()
                    : taskKey.trim(),
            taskType,
            stateScope:
                taskType === TaskDefinitionType.MANUAL ? manualScope : undefined,
            ...(createScope === "organization"
                ? { organizationId: createOrganizationId }
                : {}),
            ...(createScope === "device"
                ? {
                    organizationId: resolvedCreateOrganizationIdForSubmit,
                    deviceId: createDeviceId,
                }
                : {}),
        });
    };

    const handleConfigureDisabledTargets = () => {
        if (!configureDisabledGroup) return;
        configureDisabledTargetsMutation.mutate({
            taskKey: configureDisabledGroup.taskKey,
            organizationIds: configuredOrganizationIds,
            deviceIds: configuredDeviceIds,
        });
    };

    if (!canManage) {
        return (
            <Box className="flex flex-col gap-4">
                <PageHeader
                    pageTitleComponent={
                        <PageBreadcrumb items={[{ label: "Admin" }, { label: "Task Definitions" }]} />
                    }
                />
                <Typography>
                    You do not have access to manage task definitions.
                </Typography>
            </Box>
        );
    }

    return (
        <Box className="flex flex-col gap-4">
            <PageHeader
                pageTitleComponent={
                    <PageBreadcrumb items={[{ label: "Admin" }, { label: "Task Definitions" }]} />
                }
                rightButtons={
                    <Button
                        variant="text"
                        size="small"
                        color="inherit"
                        startIcon={<AddOutlined />}
                        onClick={openBaseDialog}
                    >
                        New Definition
                    </Button>
                }
            />
            <div className="py-2 px-3 gap-4 flex flex-col">

                <AdminConfigurationNav orgId={orgId} active="task-definitions" />

                <Typography variant="body2" color="text.secondary">
                    Create definitions at global, organization, or device scope. You can
                    also create overrides from a base definition to customize title,
                    placement, or visibility for a specific organization or device.
                </Typography>

                <TextField
                    label="Search by title or key"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    fullWidth
                />

                <Stack spacing={2}>
                    {filteredGroups.map((group) => (
                        <Box
                            key={group.taskKey}
                            className="rounded-md border border-slate-200 bg-white p-4"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex flex-col gap-1">
                                    <Typography variant="subtitle1">
                                        {group.base?.title ?? group.taskKey}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Task key: {group.taskKey}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Disabled organizations: {getDefinitionGroupDisabledTargets(group).organizationIds.length}
                                        {" · "}Disabled devices: {getDefinitionGroupDisabledTargets(group).deviceIds.length}
                                    </Typography>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={() => openConfigureDisabledDialog(group)}
                                        disabled={[group.base, ...group.overrides].some((definition) => definition && !isTaskTypeEditable(definition.taskConfig.taskType))}
                                    >
                                        Configure Disabled
                                    </Button>
                                    {group.base && (
                                        <Button
                                            startIcon={<LayersOutlined />}
                                            onClick={() => openOverrideDialog(group.base!)}
                                            disabled={!isTaskTypeEditable(group.base.taskConfig.taskType)}
                                            variant="outlined"
                                        >
                                            Create override
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <Divider className="my-3" />
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Title</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>State Scope</TableCell>
                                        <TableCell>Scope</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {group.base && (
                                        <TableRow key={`${group.taskKey}-base`}>
                                            <TableCell>{group.base.title}</TableCell>
                                            <TableCell>{group.base.taskConfig.taskType}</TableCell>
                                            <TableCell>{group.base.taskConfig.stateScope}</TableCell>
                                            <TableCell>
                                                {formatScopeLabel(group.base, orgNameById, deviceById)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    aria-label="Edit task definition"
                                                    disabled={!isTaskTypeEditable(group.base.taskConfig.taskType)}
                                                    onClick={() => openEditDialog(group.base!)}
                                                >
                                                    <EditOutlined />
                                                </IconButton>
                                                <IconButton
                                                    aria-label="Delete task definition"
                                                    disabled={!isTaskTypeEditable(group.base.taskConfig.taskType)}
                                                    onClick={() => setConfirmDelete(group.base!)}
                                                >
                                                    <DeleteOutline />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {group.overrides.map((definition) => (
                                        <TableRow key={definition.id}>
                                            <TableCell>{definition.title}</TableCell>
                                            <TableCell>{definition.taskConfig.taskType}</TableCell>
                                            <TableCell>{definition.taskConfig.stateScope}</TableCell>
                                            <TableCell>
                                                {formatScopeLabel(definition, orgNameById, deviceById)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    aria-label="Edit task definition"
                                                    disabled={!isTaskTypeEditable(definition.taskConfig.taskType)}
                                                    onClick={() => openEditDialog(definition)}
                                                >
                                                    <EditOutlined />
                                                </IconButton>
                                                <IconButton
                                                    aria-label="Delete task definition"
                                                    disabled={!isTaskTypeEditable(definition.taskConfig.taskType)}
                                                    onClick={() => setConfirmDelete(definition)}
                                                >
                                                    <DeleteOutline />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!group.base && group.overrides.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <Typography variant="body2">
                                                    No definitions found for this task key.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    ))}
                    {!isLoading && filteredGroups.length === 0 && (
                        <Typography variant="body2">No task definitions found.</Typography>
                    )}
                </Stack>
            </div>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {dialogMode === "edit"
                        ? "Edit Task Definition"
                        : dialogMode === "override"
                            ? "Create Override"
                            : "Create Task Definition"}
                </DialogTitle>
                <DialogContent className="flex flex-col gap-4 pt-2">
                    {dialogMode === "base" && (
                        <FormSelect
                            element={buildSelectElement(
                                "create-scope",
                                "Scope",
                                createScopeOptions.map((option) => option.label),
                            )}
                            label="Scope"
                            value={createScopeLabel}
                            onChange={(_, value) => {
                                const selected = createScopeOptions.find(
                                    (option) => option.label === value.answer,
                                );
                                if (selected) {
                                    setCreateScope(selected.value);
                                }
                            }}
                        />
                    )}

                    {dialogMode === "base" && createScope === "organization" && (
                        <Autocomplete
                            options={adminOrganizations}
                            getOptionLabel={(option) => option.name}
                            getOptionKey={(option) => option.id}
                            value={selectedCreateOrganization}
                            onChange={(_, value) => {
                                setCreateOrganizationId(value?.id ?? "");
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Organization" />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                    )}

                    {dialogMode === "base" && createScope === "device" && (
                        <Autocomplete
                            options={adminDevices}
                            getOptionLabel={(option) =>
                                option.organizationName
                                    ? `${option.organizationName} — ${option.name}`
                                    : option.name
                            }
                            getOptionKey={(option) => option.id}
                            value={selectedCreateDevice}
                            onChange={(_, value) => {
                                setCreateDeviceId(value?.id ?? "");
                                if (value?.organizationId) {
                                    setCreateOrganizationId(value.organizationId);
                                }
                            }}
                            renderInput={(params) => <TextField {...params} label="Device" />}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                    )}

                    {dialogMode === "override" && (
                        <FormSelect
                            element={buildSelectElement(
                                "override-scope",
                                "Override scope",
                                overrideScopeOptions.map((option) => option.label),
                            )}
                            label="Override scope"
                            value={overrideScopeLabel}
                            onChange={(_, value) => {
                                const selected = overrideScopeOptions.find(
                                    (option) => option.label === value.answer,
                                );
                                if (selected) {
                                    setOverrideScope(selected.value);
                                }
                            }}
                        />
                    )}

                    {dialogMode === "override" && overrideScope === "organization" && (
                        <Autocomplete
                            options={adminOrganizations}
                            getOptionLabel={(option) => option.name}
                            getOptionKey={(option) => option.id}
                            value={selectedOverrideOrganization}
                            onChange={(_, value) => {
                                setOverrideOrganizationId(value?.id ?? "");
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Organization" />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                    )}

                    {dialogMode === "override" && overrideScope === "device" && (
                        <Autocomplete
                            options={adminDevices}
                            getOptionLabel={(option) =>
                                option.organizationName
                                    ? `${option.organizationName} — ${option.name}`
                                    : option.name
                            }
                            getOptionKey={(option) => option.id}
                            value={selectedOverrideDevice}
                            onChange={(_, value) => {
                                setOverrideDeviceId(value?.id ?? "");
                                if (value?.organizationId) {
                                    setOverrideOrganizationId(value.organizationId);
                                }
                            }}
                            renderInput={(params) => <TextField {...params} label="Device" />}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                    )}

                    <FormSelect
                        element={buildSelectElement(
                            "task-type",
                            "Task Type",
                            taskTypeOptions.map((option) => option.label),
                        )}
                        label="Task Type"
                        value={taskTypeLabel}
                        disabled={dialogMode !== "base"}
                        onChange={(_, value) => {
                            const selected = taskTypeOptions.find(
                                (option) => option.label === value.answer,
                            );
                            if (selected) {
                                setTaskType(selected.value);
                            }
                        }}
                    />

                    {taskType === TaskDefinitionType.DOCUMENT && dialogMode === "base" && (
                        <Autocomplete
                            options={templates}
                            getOptionLabel={(option) =>
                                `${option.defaultTitle} (${option.templateKey})`
                            }
                            value={selectedTemplate}
                            onChange={(_, value) => {
                                setSelectedTemplate(value);
                                setTaskKey(value?.templateKey ?? "");
                                setTaskKeyError(null);
                            }}
                            renderInput={(params) => (
                                <TextField {...params} label="Document Template" />
                            )}
                            isOptionEqualToValue={(option, value) =>
                                option.templateKey === value.templateKey
                            }
                        />
                    )}
                    {taskType === TaskDefinitionType.DOCUMENT && dialogMode !== "base" && (
                        <TextField
                            label="Document Template"
                            value={taskKey}
                            fullWidth
                            disabled
                        />
                    )}

                    {taskType === TaskDefinitionType.MANUAL && (
                        <FormSelect
                            element={buildSelectElement(
                                "manual-scope",
                                "Manual Task Scope",
                                manualScopeOptions.map((option) => option.label),
                            )}
                            label="Manual Task Scope"
                            value={manualScopeLabel}
                            disabled={dialogMode !== "base"}
                            onChange={(_, value) => {
                                const selected = manualScopeOptions.find(
                                    (option) => option.label === value.answer,
                                );
                                if (selected) {
                                    setManualScope(selected.value);
                                }
                            }}
                        />
                    )}

                    <TextField
                        label="Task Key"
                        value={taskKey}
                        onChange={(event) => {
                            setTaskKey(event.target.value);
                            setTaskKeyError(null);
                        }}
                        fullWidth
                        error={Boolean(taskKeyError)}
                        helperText={taskKeyError ?? ""}
                        disabled={dialogMode !== "base" || taskType === TaskDefinitionType.DOCUMENT}
                    />

                    <TextField
                        label="Title"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        fullWidth
                    />

                    <Autocomplete
                        options={cardOptions}
                        getOptionLabel={(option) =>
                            `${option.stageTitle} — ${option.title}`
                        }
                        value={selectedCardOption}
                        onChange={(_, value) => setSelectedCardOption(value)}
                        renderInput={(params) => (
                            <TextField {...params} label="Parent Card" />
                        )}
                        isOptionEqualToValue={(option, value) =>
                            option.cardKey === value.cardKey
                        }
                    />

                    {taskType === TaskDefinitionType.MANUAL && (
                        <Box>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                className="mb-1 block"
                            >
                                Description
                            </Typography>
                            <RawEditor
                                ref={descriptionEditorRef}
                                markdown={description}
                                onChange={setDescription}
                                autoFocus={false}
                            />
                        </Box>
                    )}

                    <TextField
                        label="Order"
                        value={order}
                        onChange={(event) => setOrder(event.target.value)}
                        type="number"
                        fullWidth
                    />

                    <FormControl component="fieldset">
                        <Typography variant="subtitle2">Enabled</Typography>
                        <RadioGroup
                            row
                            value={enabled ? "true" : "false"}
                            onChange={(event) => setEnabled(event.target.value === "true")}
                        >
                            <FormControlLabel value="true" control={<Radio />} label="Yes" />
                            <FormControlLabel value="false" control={<Radio />} label="No" />
                        </RadioGroup>
                    </FormControl>

                    <Box>
                        <Typography variant="subtitle2" className="mb-2">
                            Visibility conditions
                        </Typography>
                        <FormGroup className="grid grid-cols-2 gap-x-4">
                            {VISIBILITY_CONDITION_OPTIONS.map((option) => (
                                <FormControlLabel
                                    key={option.key}
                                    control={
                                        <Checkbox
                                            checked={visibilityConditions[option.key] === true}
                                            onChange={() => toggleVisibility(option.key)}
                                        />
                                    }
                                    label={option.label}
                                />
                            ))}
                        </FormGroup>
                    </Box>

                    {formError && <Typography color="error">{formError}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={createMutation.isPending || updateMutation.isPending}
                    >
                        {dialogMode === "edit" ? "Update" : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!configureDisabledGroup}
                onClose={handleCloseConfigureDisabledDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Configure Disabled Targets</DialogTitle>
                <DialogContent className="flex flex-col gap-3 pt-2">
                    <Typography variant="body2" color="text.secondary">
                        Select organizations and devices where this task should be hidden.
                    </Typography>
                    <Autocomplete
                        multiple
                        options={adminOrganizations}
                        getOptionLabel={(option) => option.name}
                        getOptionKey={(option) => option.id}
                        value={selectedConfiguredOrganizations}
                        renderTags={() => null}
                        onChange={(_, value) =>
                            setConfiguredOrganizationIds(
                                value.map((organization) => organization.id),
                            )
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Disabled organizations" />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                    <Box className="flex flex-wrap gap-1">
                        {selectedConfiguredOrganizations.map((organization) => (
                            <Chip
                                key={organization.id}
                                label={organization.name}
                                size="small"
                                onDelete={() =>
                                    setConfiguredOrganizationIds((prev) =>
                                        prev.filter((id) => id !== organization.id),
                                    )
                                }
                            />
                        ))}
                    </Box>
                    <Autocomplete
                        multiple
                        options={adminDevices}
                        getOptionLabel={(option) =>
                            option.organizationName
                                ? `${option.organizationName} — ${option.name}`
                                : option.name
                        }
                        getOptionKey={(option) => option.id}
                        value={selectedConfiguredDevices}
                        renderTags={() => null}
                        onChange={(_, value) =>
                            setConfiguredDeviceIds(value.map((device) => device.id))
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Disabled devices" />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                    <Box className="flex flex-wrap gap-1">
                        {selectedConfiguredDevices.map((device) => (
                            <Chip
                                key={device.id}
                                label={
                                    device.organizationName
                                        ? `${device.organizationName} — ${device.name}`
                                        : device.name
                                }
                                size="small"
                                onDelete={() =>
                                    setConfiguredDeviceIds((prev) =>
                                        prev.filter((id) => id !== device.id),
                                    )
                                }
                            />
                        ))}
                    </Box>
                    {configureError && <Typography color="error">{configureError}</Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfigureDisabledDialog} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfigureDisabledTargets}
                        disabled={configureDisabledTargetsMutation.isPending}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Task Definition</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete{" "}
                        {confirmDelete?.title ?? "this task"}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => {
                            if (confirmDelete) {
                                deleteMutation.mutate(confirmDelete.id);
                            }
                        }}
                        disabled={deleteMutation.isPending}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
